import { LearnerPageChrome } from "@/pages/learners/page-chrome"
import { LearnersReliefListingsReliefListingRequest } from "@/types/serializers"
import { useTranslation } from "react-i18next"
import { PageBody } from "@/components/ui/layout"
import { BadgeCheck, Ban, CircleCheck, Inbox, User } from "lucide-react"
import {
  Empty,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyHeader,
  EmptyContent,
} from "@/components/ui/empty"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { buttonVariants } from "@/components/ui/button-variants"
import { Link, useProps } from "@/lib/page-rendering"

type IndexProps = {
  requests: Array<LearnersReliefListingsReliefListingRequest>
  relief_listing_title: string
}

const useIndexProps = () => useProps<IndexProps>()

export default function Page() {
  const { requests, relief_listing_title } = useIndexProps()
  const { t } = useTranslation("school_relief_listings", {
    keyPrefix: "requests.index",
  })
  const { t: tRequestType } = useTranslation("school_relief_listings", {
    keyPrefix: "requests.types",
  })
  const { t: tRequestStatus } = useTranslation("school_relief_listings", {
    keyPrefix: "requests.status",
  })

  return (
    <>
      <LearnerPageChrome identity={{ name: relief_listing_title }} />
      <PageBody>
        <Card>
          <CardContent>
            {requests.length === 0 ? (
              <Empty data-testid="relief-listing-requests-empty">
                <EmptyHeader>
                  <EmptyMedia
                    variant="icon"
                    className="text-muted size-20 bg-white"
                  >
                    <Inbox className="size-16" />
                  </EmptyMedia>
                  <EmptyTitle>{t("empty_list_title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("empty_list_description")}
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Button asChild>
                    <Link
                      href={
                        "/search/educators?relief_availability.keyword=open_to_invitations"
                      }
                    >
                      {t("invite")}
                    </Link>
                  </Button>
                </EmptyContent>
              </Empty>
            ) : (
              <ul className="grid w-full grid-cols-1 gap-4 pt-6 sm:grid-cols-2">
                {requests.map((request) => (
                  <li key={request.id}>
                    <Card data-testid="request-list-card">
                      <CardHeader>
                        <div className="flex flex-row flex-wrap gap-4">
                          {request.state === "accepted" && (
                            <CircleCheck className="fill-brand-wise-green" />
                          )}
                          <CardTitle>{request.listing_profile_name}</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-row flex-wrap gap-4">
                          <Badge data-testid="request-type">
                            {tRequestType(request.type)}
                          </Badge>
                          <Badge
                            data-testid="request-state"
                            variant={
                              request.state === "accepted"
                                ? "accepted"
                                : "default"
                            }
                          >
                            {tRequestStatus(request.state)}
                          </Badge>
                        </div>
                      </CardContent>
                      <CardFooter className="flex flex-row flex-wrap gap-4">
                        <Button asChild>
                          <Link
                            href={`/search/educators/${request.listing_profile_uuid}`}
                          >
                            <User />
                            {t("view_listing")}
                          </Link>
                        </Button>

                        {request.under_consideration &&
                          request.type === "invitation" && (
                            <AllowAcceptanceDialog
                              type="invitation"
                              href={`/learners/relief_listings/invitations/${request.id}`}
                              method="patch"
                              data={{ status: "ready_to_accept" }}
                            />
                          )}

                        {request.under_consideration &&
                          request.type === "application" && (
                            <AllowAcceptanceDialog
                              type="application"
                              href={`/learners/relief_listings/applications/${request.id}/response`}
                              method="post"
                              data={{ response: "ready_to_accept" }}
                            />
                          )}

                        {request.cancellable &&
                          request.type === "invitation" && (
                            <ConfirmationDialog
                              description={t("actions.cancel_description")}
                              action={
                                <Link
                                  className={buttonVariants({
                                    variant: "destructive",
                                  })}
                                  method="patch"
                                  href={`/learners/relief_listings/invitations/${request.id}`}
                                  data={{ status: "cancelled" }}
                                  as={Button}
                                >
                                  {t("actions.cancel")}
                                </Link>
                              }
                            >
                              <ConfirmationDialogTrigger asChild>
                                <Button variant={"destructive-outline"}>
                                  <Ban />
                                  {t("actions.cancel")}
                                </Button>
                              </ConfirmationDialogTrigger>
                            </ConfirmationDialog>
                          )}

                        {request.rejectable &&
                          request.type === "application" && (
                            <ConfirmationDialog
                              description={t("actions.reject_description")}
                              action={
                                <Link
                                  className={buttonVariants({
                                    variant: "destructive",
                                  })}
                                  method="post"
                                  href={`/learners/relief_listings/applications/${request.id}/response`}
                                  data={{ response: "reject" }}
                                  as={Button}
                                >
                                  {t("actions.reject")}
                                </Link>
                              }
                            >
                              <ConfirmationDialogTrigger asChild>
                                <Button variant={"destructive-outline"}>
                                  <Ban />
                                  {t("actions.reject")}
                                </Button>
                              </ConfirmationDialogTrigger>
                            </ConfirmationDialog>
                          )}
                      </CardFooter>
                    </Card>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}

function AllowAcceptanceDialog({
  href,
  method,
  data,
  type,
}: Pick<React.ComponentProps<typeof Link>, "href" | "data" | "method"> & {
  type: LearnersReliefListingsReliefListingRequest["type"]
}) {
  const { t } = useTranslation("school_relief_listings", {
    keyPrefix: "requests.index",
  })
  return (
    <ConfirmationDialog
      description={t("actions.allow_acceptance_description", { type })}
      action={
        <Link method={method} href={href} data={data} as={Button}>
          {t("actions.allow_acceptance")}
        </Link>
      }
    >
      <ConfirmationDialogTrigger asChild>
        <Button>
          <BadgeCheck />
          {t("actions.allow_acceptance")}
        </Button>
      </ConfirmationDialogTrigger>
    </ConfirmationDialog>
  )
}
